import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Loader2, ChevronRight, Layers, Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';

const inr = (n) => (n === null || n === undefined ? '—' : `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`);
const pct = (n) => (n === null || n === undefined ? '—' : `${n}%`);
const BAND_CLS = {
  A: 'bg-emerald-50 text-emerald-700 border-emerald-200', B: 'bg-sky-50 text-sky-700 border-sky-200',
  C: 'bg-amber-50 text-amber-700 border-amber-200', D: 'bg-rose-50 text-rose-700 border-rose-200',
  '—': 'bg-slate-100 text-slate-500 border-slate-200',
};
const TONE = ['border-l-violet-500', 'border-l-emerald-500', 'border-l-sky-500', 'border-l-amber-500', 'border-l-rose-500', 'border-l-slate-400'];

/** Technology-wise roll-up: one block per Books category, trainers inside it. */
export default function PerformanceCategoryView({ periodParams, picked, onOpenTrainer }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('rescore');
  const [shut, setShut] = useState({});

  const key = JSON.stringify({ periodParams, picked, mode });
  useEffect(() => {
    let dead = false;
    setLoading(true);
    api.get('/training/trainer-performance/by-category', {
      params: { ...periodParams, mode, trainers: picked.length ? picked.join(',') : undefined },
    })
      .then((r) => { if (!dead) setD(r.data); })
      .catch((e) => { if (!dead) toast.error(e.response?.data?.detail || 'Could not load the technology view'); })
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const groups = d?.groups || [];
  const grand = d?.grand || {};
  const share = useMemo(() => (v) => (grand.business_ex_gst ? Math.round((v / grand.business_ex_gst) * 100) : 0), [grand]);

  return (
    <div className="space-y-3" data-testid="perf-category-view">
      <Card className="p-3.5 border bg-slate-50/70 flex flex-wrap items-center gap-3">
        <Layers className="w-4 h-4 text-violet-600" />
        <div className="flex-1 min-w-[260px]">
          <p className="text-[12.5px] font-semibold text-slate-800">
            {groups.length} categor{groups.length === 1 ? 'y' : 'ies'} · {inr(grand.business_ex_gst)} excl. GST
          </p>
          <p className="text-[11px] text-slate-500">
            The category comes from the Zoho Books category on each invoice — the same field the Revenue Report groups by.
            A trainer teaching two technologies is split across both, so the categories add up to the total.
          </p>
        </div>
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-0.5" data-testid="perf-cat-mode">
          {[['rescore', 'Scored within category'], ['filter', 'Whole-portfolio score']].map(([k, l]) => (
            <button key={k} onClick={() => setMode(k)}
              className={`px-2.5 py-1.5 text-[11.5px] font-semibold rounded-md transition-colors ${
                mode === k ? 'bg-violet-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
              data-testid={`perf-cat-mode-${k}`}>{l}</button>
          ))}
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <button className="text-slate-400 hover:text-violet-700" data-testid="perf-cat-info"><Info className="w-4 h-4" /></button>
          </PopoverTrigger>
          <PopoverContent className="w-80 bg-white text-[11.5px] text-slate-600" align="end">
            <p className="font-semibold text-slate-800 mb-1 text-[12.5px]">Two ways to read this</p>
            <p className="mb-2"><b>Scored within category</b> re-runs the index using only that technology's batches, demos and
              business, so you compare like with like — a Cloud trainer against Cloud trainers.</p>
            <p className="mb-2"><b>Whole-portfolio score</b> keeps each trainer's overall index and simply groups them, so the
              number matches the Scorecard tab.</p>
            <p className="text-slate-500">Non-technology categories are left out of this view
              {d?.hidden_categories?.length ? `: ${d.hidden_categories.join(', ')}` : ''}
              {d?.excluded?.business_ex_gst ? ` — ${inr(d.excluded.business_ex_gst)} of business sits there.` : '.'}
            </p>
          </PopoverContent>
        </Popover>
      </Card>

      {loading && (
        <Card className="border p-12 text-center text-[12px] text-slate-400" data-testid="perf-cat-loading">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Splitting the numbers by technology…
        </Card>
      )}

      {!loading && groups.length === 0 && (
        <Card className="border p-12 text-center text-[12px] text-slate-400" data-testid="perf-cat-empty">No trainer activity in this period.</Card>
      )}

      {!loading && groups.map((g, gi) => {
        const open = !shut[g.category];
        const t = g.totals;
        return (
          <Card key={g.category} className={`border border-l-4 ${TONE[gi % TONE.length]} overflow-hidden`} data-testid={`perf-cat-${g.category}`}>
            <button onClick={() => setShut((p) => ({ ...p, [g.category]: open }))}
              className="w-full flex flex-wrap items-center gap-x-5 gap-y-2 px-4 py-3 hover:bg-slate-50/80 transition-colors text-left"
              data-testid={`perf-cat-toggle-${g.category}`}>
              <ChevronRight className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
              <div className="min-w-[180px]">
                <p className="text-[14px] font-bold text-slate-900">{g.category}</p>
                <p className="text-[10.5px] text-slate-500">
                  {g.trainers} trainer{g.trainers === 1 ? '' : 's'} · {t.batches} batch(es) · {share(t.business_ex_gst)}% of business
                  {g.unmapped ? ' · no invoice yet to tell us the category' : ''}
                </p>
              </div>
              {[['Business excl. GST', inr(t.business_ex_gst)], ['Collected', pct(t.collection_pct)],
                ['Classes held', t.classes_held], ['Demos', t.demos], ['Conversion', pct(t.conversion_pct)],
                ['Avg index', t.avg_index ?? '—']].map(([l, v]) => (
                <div key={l} className="min-w-[88px]">
                  <p className="text-[9.5px] uppercase tracking-wider text-slate-400">{l}</p>
                  <p className="text-[13px] font-semibold text-slate-800 tabular-nums">{v}</p>
                </div>
              ))}
            </button>

            {open && (
              <div className="overflow-x-auto border-t border-slate-200">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-50/80 text-[9.5px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <th className="px-4 py-2 text-left">Trainer</th>
                      <th className="px-3 py-2 text-right">Index</th>
                      <th className="px-3 py-2 text-right">Business excl. GST</th>
                      <th className="px-3 py-2 text-right">Collected</th>
                      <th className="px-3 py-2 text-right">Batches</th>
                      <th className="px-3 py-2 text-right">Done</th>
                      <th className="px-3 py-2 text-right">Classes</th>
                      <th className="px-3 py-2 text-right">Class %</th>
                      <th className="px-3 py-2 text-right">Attend %</th>
                      <th className="px-3 py-2 text-right">Demos</th>
                      <th className="px-3 py-2 text-right">Conv %</th>
                      <th className="px-3 py-2 text-right">Share of cat.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.rows.map((r) => (
                      <tr key={r.name} onClick={() => r.trainer_id && onOpenTrainer(r.trainer_id)}
                        className="border-b border-slate-100 last:border-0 hover:bg-violet-50 cursor-pointer transition-colors"
                        data-testid={`perf-cat-row-${g.category}-${r.name}`}>
                        <td className="px-4 py-2.5">
                          <span className="font-semibold text-slate-800">{r.display_name || r.name}</span>
                          <span className="block text-[10px] text-slate-400">{r.employee_id ? `ID ${r.employee_id} · ` : ''}{r.courses} course(s) in this category</span>
                        </td>
                        <td className="px-3 py-2.5 text-right whitespace-nowrap">
                          <span className="tabular-nums font-bold text-slate-900">{r.index ?? '—'}</span>
                          <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[9.5px] font-bold border ${BAND_CLS[r.band] || BAND_CLS['—']}`}>{r.band}</span>
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-slate-900">{inr(r.business_ex_gst)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{pct(r.collection_pct)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{r.batches}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-emerald-700 font-semibold">{r.batches_completed}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">
                          <span className="font-semibold text-slate-800">{r.classes_held}</span>
                          <span className="text-slate-400">/{r.classes_total ?? r.classes_held}</span>
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{pct(r.dpr_pct)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{pct(r.attendance_pct)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{r.demos}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-600">{pct(r.conversion_pct)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-violet-700 font-semibold">
                          {t.business_ex_gst ? `${Math.round((r.business_ex_gst / t.business_ex_gst) * 100)}%` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        );
      })}

      {!loading && d?.excluded?.business_ex_gst > 0 && (
        <p className="text-[11px] text-slate-400 px-1" data-testid="perf-cat-excluded">
          {inr(d.excluded.business_ex_gst)} of business is billed under non-technology categories
          ({(d.hidden_categories || []).join(', ')}) and is left out of this view — it still counts on the Scorecard tab,
          where the total is {inr(d.excluded.total_business)}.
        </p>
      )}
    </div>
  );
}
